import React, { useState } from 'react';
import { useFormContext, Controller, useFieldArray } from 'react-hook-form';
import Select from 'react-select';
import Input from '../../../forms/input.component';
import TextArea from '../../../forms/text-area.component';
import Checkbox from '../../../forms/checkbox.component';
import Button from '../../../forms/button.component';
import Icon from '../../../forms/icon.component';
import Title from '../../../forms/titles.component';
import InvalidFeedback from '../../../forms/invalid-feedback.component';
import useInputValidation from '../../../../hooks/use-input-validation.hook';
import { AlertService } from '../../../../services/alert.service';

const VALIDATOR_OPTIONS = [
  { value: 'NOT_MATCH_MENU', label: 'Sin coincidencias' },
  { value: 'EMAIL', label: 'Correo' },
  { value: 'NUMBER', label: 'Número' },
  { value: 'DATE', label: 'Fecha' },
  { value: 'URL', label: 'URL' },
  { value: 'PATTERN', label: 'Expresión regular' },
  { value: 'WEBHOOK', label: 'Webhook' }
];

const TIME_OPTIONS = Array.from({ length: 12 }, (_, i) => ({
  value: (i + 1) * 5,
  label: `${(i + 1) * 5} min`
}));

export const RulesConfig: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const {
    control,
    register,
    watch,
    getValues,
    setValue,
    formState: { errors }
  } = useFormContext();

  const {
    fields: validators,
    append: appendValidator,
    remove: removeValidator
  } = useFieldArray({ control, name: 'interactive.validators' });
  const {
    fields: triggers,
    append: appendTrigger,
    remove: removeTrigger
  } = useFieldArray({ control, name: 'interactive.triggers' });

  const attempts = watch('interactive.attempts_before_close.attempts');
  const attemptType = watch('interactive.attempts_before_close.type');
  const attemptsClose = watch('interactive.attempts_before_close.close.category');
  const attemptsMessage = watch('interactive.attempts_before_close.message.es');
  const hasCloseTrigger = watch('interactive.triggers')?.some((t: any) => t.close) ?? false;

  const { urlRegex } = useInputValidation();
  const [editingValidatorIndex, setEditingValidatorIndex] = useState<number | null>(null);
  const [editingTriggerIndex, setEditingTriggerIndex] = useState<number | null>(null);
  const isEditingValidator = editingValidatorIndex !== null;
  const isEditingTrigger = editingTriggerIndex !== null;

  const startAdd = () => {
    appendValidator({ type: '', config: {}, message: { es: '' } });
    setEditingValidatorIndex(validators.length);
  };

  const cancelEdit = () => {
    if (editingValidatorIndex === null) return;

    const current = getValues(`interactive.validators.${editingValidatorIndex}`) as {
      type?: string;
      message?: { es?: string };
    };

    const isNewValidator = !current.type && (!current.message || !current.message.es);
    if (isNewValidator) {
      removeValidator(editingValidatorIndex);
    }
    setEditingValidatorIndex(null);
  };

  const saveEdit = () => {
    setEditingValidatorIndex(null);
  };

  const startAddTrigger = () => {
    appendTrigger({ after_minutes: null, text: { es: '' }, close: false, webhook: false });
    setEditingTriggerIndex(triggers.length);
  };

  const cancelEditTrigger = () => {
    if (editingTriggerIndex === null) return;

    const currentTrigger = getValues(`interactive.triggers.${editingTriggerIndex}`) as {
      after_minutes?: number;
      text?: { es?: string };
      close?: boolean;
      webhook?: boolean;
    };

    const isNewTrigger =
      !currentTrigger.after_minutes &&
      (!currentTrigger.text || !currentTrigger.text.es) &&
      !currentTrigger.close &&
      !currentTrigger.webhook;

    if (isNewTrigger) {
      removeTrigger(editingTriggerIndex);
    }

    setEditingTriggerIndex(null);
  };

  const saveEditTrigger = () => {
    setEditingTriggerIndex(null);
  };

  const hasAttempts = attempts > 0 && !!attemptType && !!attemptsClose && !!attemptsMessage;

  const rulesDone = hasAttempts && !isEditingValidator && !isEditingTrigger;

  return (
    <div
      className="modal fade show card d-block rules-interactive-modal"
      data-blendy-to="nodeRulesModal"
    >
      <div className="card-body position-relative pt-7">
        <div
          className="position-absolute top-0 end-0 m-2 btn btn-icon btn-icon-danger fs-2x"
          onClick={
            rulesDone
              ? onClose
              : () => AlertService.toastWarning('Debes completar las reglas antes de cerrar')
          }
        >
          <Icon icon="ki-cross" size="fs-2x" />
        </div>

        <Title
          icon="ki-check-square"
          title="Reglas de interacción"
          subtitle="Configura las reglas de validación y las acciones a realizar cuando se cumplan."
          type="info"
        />

        <div className="row gx-4 border-top pt-5">
          {/* 1. Intentos antes de cerrar */}
          <div className="col-md-4 ">
            <div className="card h-100">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h6 className="mb-0">Intentos antes de cerrar</h6>
              </div>
              <div className="card-body d-flex flex-column pt-1">
                <label className="form-label">
                  Número de intentos<span className="required"></span>
                </label>
                <div className="d-flex gap-2">
                  <div className="mw-70px">
                    <Input
                      type="number"
                      requiredIndicator="required"
                      {...register('interactive.attempts_before_close.attempts', {
                        valueAsNumber: true,
                        required: 'Requerido'
                      })}
                      errors={(errors.interactive as any)?.attempts_before_close?.attempts?.message}
                    />
                  </div>

                  <Controller
                    control={control}
                    name="interactive.attempts_before_close.type"
                    rules={{ required: 'Requerido' }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        options={[{ value: 'CLOSE', label: 'Cerrar ticket' }]}
                        onChange={(opt) => field.onChange(opt?.value)}
                        value={
                          [{ value: 'CLOSE', label: 'Cerrar ticket' }].find(
                            (o) => o.value === field.value
                          ) || null
                        }
                        className="form-select-custom mb-2"
                        classNamePrefix="form-select-custom"
                        placeholder="Acción"
                      />
                    )}
                  />
                  <InvalidFeedback
                    error={(errors.interactive as any)?.attempts_before_close?.type?.message}
                  />
                </div>

                {attemptType === 'CLOSE' && (
                  <Input
                    label="Categoría de cierre"
                    requiredIndicator="required"
                    placeholder="Selecciona una categoria"
                    {...register('interactive.attempts_before_close.close.category', {
                      required: 'Requerido'
                    })}
                    errors={(errors.interactive as any)?.attempts_before_close?.close?.category}
                  />
                )}

                <TextArea
                  label="Mensaje tras agotar intentos"
                  requiredIndicator="required"
                  placeholder="Escribe el mensaje"
                  rows={2}
                  {...register('interactive.attempts_before_close.message.es', {
                    required: 'Requerido'
                  })}
                  counter={1024}
                  value={watch('interactive.attempts_before_close.message.es')?.length || 0}
                  errors={(errors.interactive as any)?.attempts_before_close?.message?.es?.message}
                />
              </div>
            </div>
          </div>
          {/* 2. Validaciones */}
          <div className="col-md-4">
            <div className="card pb-3">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h6 className="mb-0">Validaciones</h6>
                {editingValidatorIndex === null && (
                  <Button type="button" className="btn btn-sm btn-light-primary" onClick={startAdd}>
                    + Agregar
                  </Button>
                )}
              </div>
              {editingValidatorIndex !== null
                ? (() => {
                    const i = editingValidatorIndex;
                    const typeVal = watch(`interactive.validators.${i}.type`);
                    const msgVal = watch(`interactive.validators.${i}.message.es`) || '';
                    const patternVal = watch(`interactive.validators.${i}.config.pattern`) || '';
                    const urlDevVal =
                      watch(`interactive.validators.${i}.config.webhook.url_dev`) || '';
                    const urlProdVal =
                      watch(`interactive.validators.${i}.config.webhook.url_prod`) || '';

                    let valid = !!typeVal && msgVal.trim().length > 0;

                    if (typeVal === 'PATTERN') {
                      const regexPattern = /^\/.*\/[gimuy]*$/;
                      valid = valid && regexPattern.test(patternVal);
                    }

                    if (typeVal === 'WEBHOOK') {
                      valid = valid && urlRegex.test(urlDevVal) && urlRegex.test(urlProdVal);
                    }

                    const isValid = valid;
                    return (
                      <div className="p-3 border rounded-4">
                        <Controller
                          control={control}
                          name={`interactive.validators.${i}.type`}
                          rules={{ required: 'Requerido' }}
                          render={({ field }) => (
                            <Select
                              {...field}
                              options={VALIDATOR_OPTIONS}
                              onChange={(opt) => field.onChange(opt?.value)}
                              value={VALIDATOR_OPTIONS.find((o) => o.value === field.value) || null}
                              className="form-select-custom mb-2"
                              classNamePrefix="form-select-custom"
                              menuPosition="fixed"
                              placeholder="Tipo de validación"
                            />
                          )}
                        />
                        <InvalidFeedback
                          error={(errors.interactive as any)?.validators?.[i]?.type?.message}
                        />

                        {watch(`interactive.validators.${i}.type`) === 'PATTERN' && (
                          <Input
                            label="Regex"
                            placeholder="Escribe el patrón"
                            requiredIndicator="required"
                            {...register(`interactive.validators.${i}.config.pattern` as const, {
                              required: 'Requerido',
                              pattern: {
                                value: /^\/.*\/[gimuy]*$/,
                                message: 'Debe ser una expresión regular válida'
                              }
                            })}
                            errors={
                              (errors.interactive as any)?.validators?.[i]?.config?.pattern?.message
                            }
                          />
                        )}

                        {watch(`interactive.validators.${i}.type`) === 'WEBHOOK' && (
                          <>
                            <Input
                              label="URL producción"
                              placeholder="https://..."
                              requiredIndicator="required"
                              {...register(
                                `interactive.validators.${i}.config.webhook.url_prod` as const,
                                {
                                  required: 'Requerido',
                                  pattern: {
                                    value: /https?:\/\/.+/,
                                    message: 'URL inválida'
                                  }
                                }
                              )}
                              errors={
                                (errors.interactive as any)?.validators?.[i]?.config?.webhook
                                  ?.url_prod?.message
                              }
                            />
                            <Input
                              label="URL desarrollo"
                              placeholder="https://..."
                              requiredIndicator="required"
                              {...register(
                                `interactive.validators.${i}.config.webhook.url_dev` as const,
                                {
                                  required: 'Requerido',
                                  pattern: {
                                    value: /https?:\/\/.+/,
                                    message: 'URL inválida'
                                  }
                                }
                              )}
                              errors={
                                (errors.interactive as any)?.validators?.[i]?.config?.webhook
                                  ?.url_dev?.message
                              }
                            />
                          </>
                        )}

                        <TextArea
                          label="Mensaje feedback"
                          rows={2}
                          requiredIndicator="required"
                          placeholder="Escribe el mensaje"
                          {...register(`interactive.validators.${i}.message.es` as const, {
                            required: 'Requerido'
                          })}
                          counter={1024}
                          value={watch(`interactive.validators.${i}.message.es`)?.length || 0}
                          errors={
                            (errors.interactive as any)?.validators?.[i]?.message?.es?.message
                          }
                        />
                        <div className="d-flex align-items-center justify-content-between my-2 w-100">
                          <Checkbox
                            label="Mostrar mensajes de este bloque tras un error de validación"
                            {...register(
                              `interactive.validators.${i}.show_messages_again` as const
                            )}
                            spacingEnd="3"
                            errors={
                              (errors.interactive as any)?.validators?.[i]?.show_messages_again
                                ?.message
                            }
                          />
                        </div>
                        <div className="mt-3 d-flex justify-content-end gap-2 mt-2">
                          <Button
                            type="button"
                            className="btn btn-sm btn-light-secondary"
                            onClick={cancelEdit}
                          >
                            Cancelar
                          </Button>
                          <Button
                            type="button"
                            className="btn btn-sm btn-primary"
                            onClick={saveEdit}
                            disabled={!isValid}
                          >
                            Guardar
                          </Button>
                        </div>
                      </div>
                    );
                  })()
                : (() => {
                    const validatorValues: Array<{ type?: string; message?: { es?: string } }> =
                      watch('interactive.validators');

                    return (
                      <ul className="list-group list-group-flush">
                        {validatorValues?.length > 0 ? (
                          validatorValues.map((v: any, i: number) => (
                            <li
                              key={v.id}
                              className="list-group-item d-flex justify-content-between align-items-center"
                            >
                              <div className="flex-grow-1">
                                <strong className="d-block text-truncate mw-150px">
                                  Validación {i + 1}:{' '}
                                  {VALIDATOR_OPTIONS.find((o) => o.value === v.type)?.label || '–'}
                                </strong>
                                {v.m}
                                <small className="d-block text-truncate mw-150px">
                                  Mensaje: {v.message.es || '— sin mensaje —'}
                                </small>
                              </div>
                              <div className="btn-group btn-group-sm">
                                <Button
                                  type="button"
                                  className="btn btn-light-warning"
                                  onClick={() => setEditingValidatorIndex(i)}
                                >
                                  <Icon icon="ki-pencil" />
                                </Button>
                                <Button
                                  type="button"
                                  className="btn btn-light-danger"
                                  onClick={() => removeValidator(i)}
                                >
                                  <Icon icon="ki-trash" />
                                </Button>
                              </div>
                            </li>
                          ))
                        ) : (
                          <li className="list-group-item text-center text-muted">
                            No hay validaciones
                          </li>
                        )}
                      </ul>
                    );
                  })()}
            </div>
          </div>

          {/* 3. Triggers por inactividad */}
          <div className="col-md-4 ">
            <div className="card pb-3">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h6 className="mb-0">
                  Notificaciones <br />
                  por inactividad
                </h6>
                {!hasCloseTrigger && editingTriggerIndex === null && (
                  <Button
                    type="button"
                    className="btn btn-sm btn-light-primary"
                    onClick={startAddTrigger}
                  >
                    + Agregar
                  </Button>
                )}
              </div>
              {editingTriggerIndex !== null
                ? (() => {
                    const i = editingTriggerIndex ?? triggers.length;
                    const isCloseChecked = watch(`interactive.triggers.${i}.close`);
                    const isWebhookChecked = watch(`interactive.triggers.${i}.webhook`);

                    const categoryVal = watch(`interactive.triggers.${i}.close.category`) || '';
                    const urlDevVal = watch(`interactive.triggers.${i}.webhook.url_dev`) || '';
                    const urlProdVal = watch(`interactive.triggers.${i}.webhook.url_prod`) || '';
                    const hasTime = !!watch(`interactive.triggers.${i}.after_minutes`);
                    const hasText = !!watch(`interactive.triggers.${i}.text.es`);
                    const validClose = isCloseChecked && categoryVal.trim().length > 0;
                    const validWebhook =
                      isWebhookChecked && urlRegex.test(urlDevVal) && urlRegex.test(urlProdVal);
                    const isValid =
                      hasTime &&
                      hasText &&
                      (!isCloseChecked || validClose) &&
                      (!isWebhookChecked || validWebhook);
                    return (
                      <div className="p-3 border rounded-4">
                        <label className="form-label">
                          Tiempo sin respuesta del usuario
                          <span className="required"></span>
                        </label>
                        <Controller
                          control={control}
                          name={`interactive.triggers.${i}.after_minutes`}
                          rules={{ required: 'Requerido' }}
                          render={({ field }) => (
                            <Select
                              {...field}
                              options={TIME_OPTIONS}
                              onChange={(opt) => field.onChange(opt?.value)}
                              value={TIME_OPTIONS.find((o) => o.value === field.value) || null}
                              className="form-select-custom mb-2"
                              classNamePrefix="form-select-custom"
                              placeholder="Minutos"
                              menuPosition="fixed"
                            />
                          )}
                        />
                        <InvalidFeedback
                          error={(errors.interactive as any)?.triggers?.[i]?.after_minutes?.message}
                        />

                        <TextArea
                          label="Mensaje"
                          requiredIndicator="required"
                          placeholder="Escribe el mensaje"
                          {...register(`interactive.triggers.${i}.text.es` as const, {
                            required: 'Requerido',
                            maxLength: { value: 1024, message: 'Máx. 1024 caracteres' }
                          })}
                          counter={1024}
                          value={watch(`interactive.triggers.${i}.text.es`)?.length || 0}
                          errors={(errors.interactive as any)?.triggers?.[i]?.text?.es?.message}
                        />

                        <div className="d-flex justify-content-between align-items-center my-2">
                          <Controller
                            control={control}
                            name={`interactive.triggers.${i}.close`}
                            render={({ field }) => (
                              <Checkbox
                                label="Cerrar ticket"
                                {...field}
                                checked={field.value}
                                spacingEnd="3"
                                onChange={() => {
                                  const newValue = !field.value;
                                  field.onChange(newValue);
                                  if (newValue) {
                                    setValue(`interactive.triggers.${i}.webhook`, false);
                                  }
                                }}
                                disabled={isWebhookChecked}
                              />
                            )}
                          />

                          <Controller
                            control={control}
                            name={`interactive.triggers.${i}.webhook`}
                            render={({ field }) => (
                              <Checkbox
                                label="Webhook"
                                {...field}
                                checked={field.value}
                                spacingEnd="2"
                                onChange={() => {
                                  const newValue = !field.value;
                                  field.onChange(newValue);
                                  if (newValue) {
                                    setValue(`interactive.triggers.${i}.close`, false);
                                  }
                                }}
                                disabled={isCloseChecked}
                              />
                            )}
                          />
                        </div>
                        {watch(`interactive.triggers.${i}.close`) && (
                          <Input
                            label="Categoría de cierre"
                            requiredIndicator="required"
                            placeholder="Selecciona una categoría"
                            {...register(`interactive.triggers.${i}.close.category` as const, {
                              required: 'Requerido'
                            })}
                            errors={(errors.interactive as any)?.triggers?.[i]?.close?.category}
                          />
                        )}
                        {watch(`interactive.triggers.${i}.webhook`) && (
                          <>
                            <Input
                              label="URL de desarrollo"
                              requiredIndicator="required"
                              placeholder="Agregar URL de desarrollo"
                              {...register(`interactive.triggers.${i}.webhook.url_dev` as const, {
                                required: 'Requerido',
                                pattern: {
                                  value: urlRegex,
                                  message: 'URL inválida'
                                }
                              })}
                              errors={
                                (errors.interactive as any)?.triggers?.[i]?.webhook?.url_dev
                                  ?.message
                              }
                            />
                            <Input
                              label="URL de producción"
                              requiredIndicator="required"
                              placeholder="Agregar URL de producción"
                              {...register(`interactive.triggers.${i}.webhook.url_prod` as const, {
                                required: 'Requerido',
                                pattern: {
                                  value: urlRegex,
                                  message: 'URL inválida'
                                }
                              })}
                              errors={
                                (errors.interactive as any)?.triggers?.[i]?.webhook?.url_prod
                                  ?.message
                              }
                            />
                          </>
                        )}
                        <div className="mt-3 d-flex justify-content-end gap-2">
                          <Button
                            type="button"
                            className="btn btn-light-secondary btn-sm"
                            onClick={cancelEditTrigger}
                          >
                            Cancelar
                          </Button>
                          <Button
                            type="button"
                            className="btn btn-primary btn-sm"
                            onClick={saveEditTrigger}
                            disabled={!isValid}
                          >
                            Guardar
                          </Button>
                        </div>
                      </div>
                    );
                  })()
                : (() => {
                    const triggerValues: Array<{
                      after_minutes?: number;
                      text?: { es?: string };
                      close?: boolean;
                      webhook?: boolean;
                    }> = watch('interactive.triggers');

                    return (
                      <ul className="list-group list-group-flush">
                        {triggerValues?.length > 0 ? (
                          triggerValues.map((v: any, i: number) => (
                            <li
                              key={v.id}
                              className="list-group-item d-flex justify-content-between align-items-center"
                            >
                              <div className="flex-grow-1">
                                <strong className="d-block text-truncate mw-150px">
                                  Notificación {i + 1}:{' '}
                                  {v.after_minutes ? `${v.after_minutes} min` : '– sin tiempo'}
                                </strong>
                                <small className="d-block text-truncate mw-150px">
                                  Mensaje: {v.text.es || '— sin mensaje —'}
                                  <Icon
                                    icon={
                                      v.close ? 'ki-minus-circle' : v.webhook ? 'ki-fasten' : ''
                                    }
                                    type="info"
                                    className="ms-2"
                                  />
                                </small>
                              </div>
                              <div className="btn-group btn-group-sm">
                                <Button
                                  type="button"
                                  className="btn btn-light-warning"
                                  onClick={() => setEditingTriggerIndex(i)}
                                >
                                  <Icon icon="ki-pencil" />
                                </Button>
                                <Button
                                  type="button"
                                  className="btn btn-light-danger"
                                  onClick={() => removeTrigger(i)}
                                >
                                  <Icon icon="ki-trash" />
                                </Button>
                              </div>
                            </li>
                          ))
                        ) : (
                          <li className="list-group-item text-center text-muted">
                            No hay notificaciones
                          </li>
                        )}
                      </ul>
                    );
                  })()}
            </div>
          </div>
        </div>

        {/* Botón Listo */}
        <div className="text-end">
          <button
            type="button"
            className="btn btn-sm btn-primary"
            onClick={onClose}
            disabled={!rulesDone}
          >
            Listo
          </button>
        </div>
      </div>
    </div>
  );
};
